/**
 * @abstract This was initially the page to manage an existing user, but eventually became the hard-coded registration page. 
 * The replacement is an application (layout) with id = 0. This needs to be defineable by the customer so they capture the data 
 * required for identity of the user. 
 * 
 * Ideally, the actor provides an identity image (ex: Driver's License, etc.), we process the image into data that creates the
 * user with a few extra bits of information such as email address and phone number and whether we can send SMS messages. 
 * 
 * The initially gathered information can be used in the associated applications
 * 
 * @author Brian Sparling
 * @deprecated
 */

import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators, AbstractControl, AsyncValidatorFn, ValidationErrors, FormControl } from '@angular/forms';
import { ContextService, LayoutInterface, ServerServiceConfig, LoginData, OptionSet, OptionDetail } from '../context.service';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs/operators';
import { Observable, ObservableInput, of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../environments/environment';

export function checkPhone(countryCodeRef: AccountSettingsComponent): AsyncValidatorFn {
  return (control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
    return countryCodeRef.http.post('$service/sms-lookup',
      {
        phone: control.value.replace(/[^+0-9]/g, ''),
        prefix: countryCodeRef.country
      }).pipe(map((result: any) => {
        if (!!result.message) {
          countryCodeRef.contextSettings.changeMessage(result.message.replace(/resource \/PhoneNumbers\//, 'number '));
        }
        return !!result.phoneNumber ? null : { invalidNumber: true };
      }));
  };
};
@Component({
  selector: 'account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.scss'],
  standalone: false
})
export class AccountSettingsComponent implements OnInit {
  @Input() isSearchOpen: boolean = false;
  /** creates the custom element properties to be set */
  login: LoginData | undefined;
  formGrouping: UntypedFormGroup | undefined;
  fieldGroupOrder: any[][] | undefined;
  phoneControl: any;
  countries: any[] | undefined;
  studentType: any[] | undefined;
  schoolName: string | undefined | null;
  country: string = 'USA';
  hide: boolean = true;
  layout: LayoutInterface | undefined | null;
  isWizardOpen: boolean = false;
  initialState: boolean = true;
  showError: boolean = false;
  isVertical: boolean = true;
  processing: boolean = false;
  matchedUsers: any[] = [];
  _selectedRow: number = 0;
  selectedTabIndex: number = 0;
  typeList: OptionDetail[] | undefined;
  passwordValidation: RegExp = /(?=.{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])).*/;
  passwordDescription: string = 'Password rules: minimum 8 characters, at least 3 of the following; lowercase, uppercase, numbers, symbols';
  selectedApplicantType: string | null = null;
  needsCoupon: boolean = false;
  _displaySearchButton: boolean = false;
  appConfig: ServerServiceConfig | undefined;
  stTrigger: Function | undefined;

  // Initialize anything that will stay around
  constructor(private fb: UntypedFormBuilder,
    public contextSettings: ContextService,
    public http: HttpClient,
    public router: Router,
    private actRoute: ActivatedRoute,
    //private dialog: MatDialog
  ) {
    this.contextSettings.subscribeTo("orientation", (isVertical: boolean) => {
      this.isVertical = isVertical;
    });
    this.contextSettings.subscribeTo("applicationInfo", (config: ServerServiceConfig) => {
      //this.contextSettings.loadPropertyVariables(config?.theme, this.elementRef.nativeElement.style);
      this.appConfig = config;
      if (config?.passwordRegEx) {
        this.passwordValidation = new RegExp(config.passwordRegEx);
      }
      if (config?.passwordHint) {
        this.passwordDescription = config.passwordHint;
      }
      if ( config?.studentTypeTrigger ) {
        try {
          this.stTrigger = new Function( config.studentTypeTrigger );
        } catch (err ) {  
          console.error( 'Error initializing studentTypeTrigger %O', err);
        }
      }
      this._displaySearchButton = !!config?.info.displaySearch;
      this.countries = config?.lookups?.Countries;
      this.typeList = config?.lookups?.['Student Category'];
      this.schoolName = config?.info?.institution;
    });

    this.contextSettings.subscribeTo("layoutIdentity", (layout: LayoutInterface) => {
      this.layout = layout;
    });
    this.contextSettings.subscribeTo("loginData", (login: LoginData) => {
      this.login = login;
    });

    this.actRoute.queryParams.subscribe(params => {
      if (!!params.type) {
        this.selectedApplicantType = params.type;
      }
    });

  }
  // Initialize observers and more dynamic values
  ngOnInit(): void {
    this.isSearchOpen = this.isSearchURL() ? true : false;
    this.contextSettings.showProgress(false);

    // TODO: Convert this to a dynamic definition vs. static (use a 0th layout = register)
    //  this.fieldGroupOrder = [['firstname', 'lastname', 'nickname', 'previousAttended'], ['email', 'sms', 'phone'], ['verify', 'password']];
    this.formGrouping = this.fb.group({
      formArray: this.fb.array(
        [this.fb.group({
          firstname: [this?.login?.firstname, Validators.required],
          lastname: [this?.login?.lastname, Validators.required],
          nickname: [this?.login?.nickname]
        }),
        this.fb.group({
          email: [this?.login?.email, Validators.compose([
            Validators.required,
            Validators.pattern(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i)
          ])],
          phone: [this?.login?.phone,
          Validators.required,
          checkPhone(this)
          ],
          sms: ['true']
        }),
        this.fb.group({
          studentType: [this.selectedApplicantType],
          verify: ['', Validators.requiredTrue],
          password: ['', Validators.compose([Validators.required, Validators.pattern(this.passwordValidation)])],
          waiver: ['']
        }),
        this.fb.group({
          searchFirstname: [this?.login?.firstname],
          searchLastname: [this?.login?.lastname],
          searchEmail: [this?.login?.email, Validators.compose([
            Validators.pattern(/(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/i)
          ])],
          searchPhone: [this?.login?.phone],
          searchEmplid: [''],
          searchDob: [''],
          authMethod: [null]

        })
        ])
    });
  }

  get hasWaiver(): boolean {
    return this.appConfig?.acceptWaiver ?? false;
  }

  get debug(): boolean {
    return !environment?.production;
  }

  get countryCode(): string {
    return !this.countries ? '+1' : this.countries?.find(countryDefn => countryDefn.value == this.country)?.phone ?? '+1';
  }
  // This pulls the array of groups from the group of arrays.... 
  get registrationPage(): AbstractControl | null | undefined {
    return this.formGrouping?.get('formArray');
  }

  /**
   * return the mask for the phone
   */
  get mask(): string {
    let isUs = this.countryCode.split('-');
    if (isUs.length > 1) {
      return '000-0000 99999';
    } else {
      return isUs[0] == '+1' ? '(000) 000-0000 99999' : '000 000 0099 999999';
    }
  }

  /**
   * Display the phone number
   * @param phone 
   * @returns 
   */
  prettyPhone(phone: HTMLInputElement) {
    return this.countryCode + ' ' + ('' + phone.value).replace(/\D/g, '').replace(/^(\d{3}) (\d{3}) (\d{4})(\d*)$/, this.country == 'USA' ? '($1) $2-$3 $4' : '$1 $2 $3 $4');
  }

  async _getData(): Promise<LoginData> {
    let formData: any = {}, dataToSend : any = {};

    this.registrationPage?.value.forEach((section: any | undefined) => Object.assign(formData, section));
    dataToSend.firstname = formData?.firstname.trim();
    dataToSend.lastname = formData?.lastname.trim();
    if (!!dataToSend.nickname) {
      dataToSend.nickname = formData.nickname.trim();
    }
    dataToSend.password = formData?.password?.trim();
    dataToSend.username = formData.email = formData?.email?.trim();
    dataToSend.phone = formData.phone.replace(/\D/g, '')?.trim(); // Strip to numbers only
    dataToSend.countryCode = this.country;
    dataToSend.studentData = {student_type : formData.studentType ?? null, waiver : formData?.waiver?.trim(), };
    // BMS: This is a really BAD way to handle this, but needs must
    if ( formData.studentType && this.stTrigger ) {
      try {
        this.stTrigger.call( dataToSend.studentData );
      } catch ( err ) {
        console.error( 'Trigger data change at Registration error %O\n%O', formData.studentType, err);
      }
    }
    //parameters.admin = this.model.admin;

    if (!!this.layout) {
      dataToSend.defaultLayout = this.layout;
    }

    return new Promise((resolve, reject) => {
      if (this.hasWaiver) {
        this.http.post<{ isValid: boolean, description?: string }>('$service/validate-waiver/', { waiverCode: formData.waiver }).subscribe({
          next: (result: { isValid: boolean, description?: string }) => {
            dataToSend.studentData.waiverValid = result?.isValid ?? false;
            resolve(dataToSend);
          },
          error: (err) => {
            console.error(err);
            dataToSend.waiverValid = false;
            resolve(dataToSend);
          }
        });
      } else {
        let isValid = true;
        ['firstname', 'lastname', 'username', 'password', 'phone'].forEach(key => {
          if ((dataToSend[key] ?? '') == '') {
            isValid = false;
          }
        });
        if (isValid) {
          resolve(dataToSend);
        } else {
          reject({ message: 'Invalid name, please add a name' });
        }
      }
    });
  }

  /**
   * Catch the submit event, register the user, immediately login and perform an email 
   * validation check. 
   */
  async sendSubmit($event: Event | null = null): Promise<boolean> {
    this.processing = true;
    let isSearch = false, dataForLogin; //$event.srcElement.textContent == 'Search';
    try {
      dataForLogin = { ...await this._getData() };
      this.contextSettings.unsetLoginData();
    } catch (err: any) {
      this.contextSettings.changeMessage(err.message);
      return false;
    }
    // This is a chain of events stacked, should be piped
    if (!!dataForLogin) {
      this.performSearch(true);
      this._unselectValues();

      this.http.post('$service/' + (isSearch ? 'find-student' : 'register'), dataForLogin).subscribe({
        error: (httpError): ObservableInput<{}> => {
          console.log('Default Config with error %O', httpError);
          this.processing = false;
          return of({ error: httpError });
        },
        next: (result: { error?: any, verdict?: string }) => {
          if ((result?.verdict ?? 'Valid') == 'Invalid') {
            console.error('Verdict is Invalid %O', result);
            this.contextSettings.changeMessage('E-mail provided is not valid, please use a different one');
          } else if (!result?.error) {
            if (isSearch) {
              this.matchedUsers = <any[]>result;
              switch (this.matchedUsers.length) {
                case 1:
                  this.contextSettings.changeMessage('One student matches search criteria');
                  break;
                case 0:
                  this.contextSettings.changeMessage('No students match search criteria');
                  break;
                default:
                  break;
              }
              this.performSearch(false);

            } else if (!!result) { // register or login
              let login = <LoginData>result;
              this.contextSettings.setLoginData(login);
              this.login = login;
              this.contextSettings.setLayoutId(login?.defaultLayout);
              // Login and redirect happens in the server-login.service
            } else {
              this.contextSettings.changeMessage('Email already registered with different name');
            }
          } else {
            this.contextSettings.changeMessage(result?.error ?? 'Email already registered with different name');
          }
          this.processing = false;
        },
      });
    }
    return true;
  }

  isSearchURL(): boolean {
    return (this.router.url.split('/')[1] === 'search');
  }

  getDisplayFields(): any[] {
    return [
      {
        label: 'First Name',
        id: 'firstname'
      },
      {
        label: 'Last Name',
        id: 'lastname'
      },
      {
        label: 'Email',
        id: 'email'
      },
      {
        label: 'Phone',
        id: 'phone'
      },
      {
        label: 'Student ID',
        id: 'entityId'
      }
    ];
  }

  sendText() {
    this.http.post('$service/send-message', {
      personId: this.matchedUsers[this._selectedRow].personId,
      message: 'verify-info',
      messageType: 'sms'
    }).subscribe({
      next: (result) => {
        this.resetSearch();
        this.closeSearch();
        this.matchedUsers = [];
        this._selectedRow = 0;
        this.contextSettings.changeMessage('Please follow the link in the text message to claim this user');
      }
    });
  }

  sendEmail() {
    this.http.post('$service/send-message', {
      personId: this.matchedUsers[this._selectedRow].personId,
      message: 'verify-info',
      messageType: 'email'
    }).subscribe({
      next: (result) => {
        this.resetSearch();
        this.closeSearch();
        this.matchedUsers = [];
        this._selectedRow = 0;
        this.contextSettings.changeMessage('Please follow the link in the email message to claim this user');
      }
    });
  }

  /**
   *  Reset the selection
   */
  _unselectValues() {
    // clear previous selection
    if (this.matchedUsers.length > 0) {
      this.matchedUsers[this._selectedRow]._selected = false;
    }
    this._selectedRow = 0;
  }
  /**
   * remember which row one we are modifying (called by template)
   * @param row 
   */
  _selectRow(index: number) {
    let row = this.matchedUsers[index];
    let prevSelectedIndex = this._selectedRow;
    this._unselectValues();
    if (index == prevSelectedIndex) {
      row._selected = false;
      row = { ...row };
      Object.keys(row).forEach(key => row[key] = null);
    } else {
      // setup memory of current row in table
      this._selectedRow = index;
      row._selected = true;
    }
  }

  get listFields(): string[] { return this.getDisplayFields().map(fld => fld.id) }

  openSearch() {
    this.isSearchOpen = !(this.isWizardOpen = false);
  }

  closeSearch() {
    this.isSearchOpen = this.isWizardOpen = false;
  }

  openWizard() {
    this.isSearchOpen = !(this.isWizardOpen = true);
    setTimeout(() => {
      this.reassignTabRole();
    })
  }

  closeWizard() {
    this.isSearchOpen = this.isWizardOpen = false;
  }

  reassignTabRole() {
    let stepperHeaderContainers = document.querySelectorAll('.mat-horizontal-stepper-header-container');
    stepperHeaderContainers.forEach(header => {
      header.setAttribute('role', 'tablist');
      header.setAttribute('aria-label', 'Registration form');
    })


    let steppers = document.querySelectorAll('.stepper-orientation-horizontal, .stepper-orientation-vertical, mat-stepper');
    steppers.forEach(stepper => {
      stepper.removeAttribute('aria-orientation');
      stepper.removeAttribute('role');
    });

    let stepperContent = document.querySelectorAll('.mat-horizontal-stepper-content');
    stepperContent.forEach(content => {
      content.removeAttribute('role');
      content.removeAttribute('aria-labelledby');
    });
  }

  resetSearch() {
    this.formGrouping?.reset();
    this.setShowError(false);
  }

  performSearch(results: boolean) {
    this.setShowError(false);

    //perform search - using results flag to mimic
    if (results == true) {
      this.setInitialState(false);
    }

    if (results == false) {
      this.setShowError(true);
    }
  }

  setInitialState(state: boolean) {
    this.initialState = state;
  }

  getShowError() {
    return this.showError;
  }

  setShowError(state: boolean) {
    this.showError = state;
  }

  onStepChange(event: any): void {
    this.selectedTabIndex = event.selectedIndex;
  }
}